import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Heart, HandHeart } from "lucide-react";

export const VOLUNTEER_AREAS = [
  { value: "educacao", label: "Educação (apoio escolar / tutoria)" },
  { value: "direito", label: "Direito / Jurídico" },
  { value: "saude", label: "Saúde (medicina, terapias)" },
  { value: "psicologia", label: "Psicologia" },
  { value: "terapias", label: "Terapias (ABA, TO, fono, psicopedagogia)" },
  { value: "administrativo", label: "Administrativo / Financeiro" },
  { value: "eventos", label: "Eventos e produção" },
  { value: "comunicacao", label: "Comunicação, design ou social media" },
  { value: "outros", label: "Outros" },
];

const EMPTY_SUPPORTER = {
  kind: "individual",
  doc_number: "",
  company_name: "",
  is_volunteer: false,
  volunteer_areas: [],
  is_donor: false,
  monthly_pledge: "",
  notes: "",
};

export function emptySupporter() {
  return { ...EMPTY_SUPPORTER };
}

/**
 * Compact supporter fields. Controlled component.
 * `value` may be null/undefined (means "not a supporter yet").
 * onChange(nextValue|null) — pass null to remove supporter profile.
 * `compact` reduces spacing for embedding in registration.
 */
export default function SupporterFields({ value, onChange, compact = false }) {
  const enabled = !!value;
  const v = value || EMPTY_SUPPORTER;

  const upd = (patch) => onChange({ ...v, ...patch });

  const toggleArea = (area) => {
    const has = v.volunteer_areas?.includes(area);
    upd({ volunteer_areas: has ? v.volunteer_areas.filter((a) => a !== area) : [...(v.volunteer_areas || []), area] });
  };

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      {/* Master toggle */}
      <div className="flex items-start gap-3">
        <Checkbox
          id="supporter-enabled"
          checked={enabled}
          onCheckedChange={(c) => onChange(c ? EMPTY_SUPPORTER : null)}
          data-testid="supporter-enabled-checkbox"
          className="mt-1"
        />
        <div>
          <Label htmlFor="supporter-enabled" className="font-heading font-bold text-base cursor-pointer flex items-center gap-2">
            <HandHeart className="h-4 w-4 text-[hsl(var(--secondary))]" /> Quero ser Apoiador(a) do Instituto Cuidar
          </Label>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
            Ajude como voluntário(a) e/ou doador(a). Você pode mudar a qualquer momento no seu perfil.
          </p>
        </div>
      </div>

      {enabled && (
        <div className="space-y-4 pl-7 border-l-2 border-[hsl(var(--secondary))]/30">
          {/* Kind */}
          <div>
            <Label className="text-sm">Você é</Label>
            <Select value={v.kind} onValueChange={(kind) => upd({ kind })}>
              <SelectTrigger data-testid="supporter-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Pessoa física</SelectItem>
                <SelectItem value="company">Pessoa jurídica / Empresa</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Company name if PJ */}
          {v.kind === "company" && (
            <div>
              <Label htmlFor="s-company">Razão social / Nome fantasia</Label>
              <Input id="s-company" value={v.company_name || ""} onChange={(e) => upd({ company_name: e.target.value })} data-testid="supporter-company-input" />
            </div>
          )}

          {/* Doc number */}
          <div>
            <Label htmlFor="s-doc">{v.kind === "company" ? "CNPJ" : "CPF"} <span className="text-[hsl(var(--muted-foreground))]">(opcional)</span></Label>
            <Input
              id="s-doc"
              value={v.doc_number || ""}
              onChange={(e) => upd({ doc_number: e.target.value })}
              placeholder={v.kind === "company" ? "00.000.000/0000-00" : "000.000.000-00"}
              data-testid="supporter-doc-input"
            />
          </div>

          {/* Roles */}
          <div className="grid sm:grid-cols-2 gap-3">
            <label htmlFor="s-vol" className="flex items-start gap-3 rounded-2xl border border-[hsl(var(--border))] p-4 hover:bg-[hsl(var(--muted))]/40 cursor-pointer">
              <Checkbox id="s-vol" checked={!!v.is_volunteer} onCheckedChange={(c) => upd({ is_volunteer: !!c })} data-testid="supporter-volunteer-checkbox" className="mt-1" />
              <div>
                <div className="font-heading font-bold">Voluntário(a)</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Doa tempo e conhecimento em atividades e projetos.</p>
              </div>
            </label>
            <label htmlFor="s-don" className="flex items-start gap-3 rounded-2xl border border-[hsl(var(--border))] p-4 hover:bg-[hsl(var(--muted))]/40 cursor-pointer">
              <Checkbox id="s-don" checked={!!v.is_donor} onCheckedChange={(c) => upd({ is_donor: !!c })} data-testid="supporter-donor-checkbox" className="mt-1" />
              <div>
                <div className="font-heading font-bold flex items-center gap-1.5">
                  Doador(a) <Heart className="h-3.5 w-3.5 text-[hsl(var(--secondary))]" />
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Contribui financeiramente para sustentar o instituto.</p>
              </div>
            </label>
          </div>

          {/* Volunteer areas */}
          {v.is_volunteer && (
            <div>
              <Label className="text-sm">Áreas em que quer atuar como voluntário(a)</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {VOLUNTEER_AREAS.map((a) => {
                  const active = v.volunteer_areas?.includes(a.value);
                  return (
                    <button
                      type="button"
                      key={a.value}
                      onClick={() => toggleArea(a.value)}
                      data-testid={`supporter-area-${a.value}`}
                      className={`text-left rounded-xl border px-3 py-2 text-sm transition-colors ${
                        active
                          ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white"
                          : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50"
                      }`}
                    >
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Monthly pledge */}
          {v.is_donor && (
            <div>
              <Label htmlFor="s-pledge">Compromisso mensal (R$) <span className="text-[hsl(var(--muted-foreground))]">— informativo</span></Label>
              <Input
                id="s-pledge"
                type="number"
                min="0"
                step="1"
                value={v.monthly_pledge ?? ""}
                onChange={(e) => upd({ monthly_pledge: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="Ex.: 50"
                data-testid="supporter-pledge-input"
              />
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Depois você pode ativar a doação recorrente automática pelo Stripe na página de doações.
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label htmlFor="s-notes">Observações (opcional)</Label>
            <Textarea
              id="s-notes"
              rows={2}
              value={v.notes || ""}
              onChange={(e) => upd({ notes: e.target.value })}
              placeholder="Disponibilidade, habilidades específicas, tempo disponível…"
              data-testid="supporter-notes-input"
            />
          </div>
        </div>
      )}
    </div>
  );
}
